import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import { SHIPPING_TYPE } from "constants/order";
import {
  useCreateOrder,
  useDistricts,
  useListCustomerByKeySearch,
  useLocationsByCustomer,
  useProvinces,
  useTicketDetail,
  useWards,
} from "hooks";
import { CustomerLocationType } from "pages/CustomerManagement/Location/utils";
import { getInitCustomerParams } from "pages/CustomerManagement/utils";
import OrdersFormV2 from "pages/Orders/OrdersForm/OrdersFormV2";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Spinner } from "reactstrap";
import MessageModal from "../../../components/shared-components/MessageModal";

const initLocationParams = () => {
  return {
    keywords: "",
    location_type: CustomerLocationType.RECEIVE,
  };
};

const initCustomerDetailParams = () => {
  return {
    customerId: "",
  };
};

const initLocalitiesParams = () => {
  return {
    countryId: 1,
    type: 1,
    parent_code: "",
  };
};

const MyOrderPageV2 = props => {
  const { t } = useTranslation(["order"]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState = /** @type {{ ticketId?: number | string }} */ (
    location?.state || {}
  );
  const ticketIdFromQuery = searchParams.get("ticketId");
  const ticketIdFromState = locationState.ticketId ?? ticketIdFromQuery;
  const ticketIdForOrder = Number(ticketIdFromState);
  const hasTicketContext =
    Number.isInteger(ticketIdForOrder) && ticketIdForOrder > 0;

  const {
    data: ticketDetail,
    isLoading: isLoadingTicketDetail,
    isError: isTicketDetailError,
  } = useTicketDetail(hasTicketContext ? ticketIdForOrder : null);

  const isHiddenShippingUnit = Boolean(ticketDetail?.is_hidden_shipping_unit);
  const isTicketDetailReady =
    !hasTicketContext || (!isLoadingTicketDetail && !isTicketDetailError);

  const [provinceParams, setProvinceParams] = useState(initLocalitiesParams);
  const [districtParams, setDistrictParams] = useState(initLocalitiesParams);
  const [wardParams, setWardParams] = useState(initLocalitiesParams);
  const [coppiedOrder, setCoppiedOrder] = useState();
  const [customerParams, setCustomerParams] = useState(getInitCustomerParams);
  const [locationParams, setLocationParams] = useState(initLocationParams);
  const [customerDetailParams, setCustomerDetailParams] = useState(
    initCustomerDetailParams
  );
  const [openWarningRunnerPopup, setOpenWarningRunnerPopup] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [orderCode, setOrderCode] = useState("");
  const [customerCode, setCustomerCode] = useState("");
  const [messagePopups, setMessagePopups] = useState([]);

  const { data: customers, refetch: listCustomer } = useListCustomerByKeySearch(
    {
      params: customerParams,
    }
  );

  useEffect(() => {
    if (customerParams && customerParams.run) {
      listCustomer();
    }
  }, [customerParams, listCustomer]);

  useEffect(() => {
    setCustomerParams(prev => ({
      ...prev,
      sizePerPage: 100,
      run: true,
      shipping_type: [
        SHIPPING_TYPE.DVVC,
        SHIPPING_TYPE.NHAXE,
        SHIPPING_TYPE.RUNNER,
      ].join(","),
      columnFilter: {
        "filter[record_status]": "O",
      },
    }));
  }, []);

  const { data: provinces, refetch: refetchProvinces } =
    useProvinces(provinceParams);
  const { data: districts, refetch: refetchDistricts } =
    useDistricts(districtParams);
  const { data: wards, refetch: refetchWards } = useWards(wardParams);
  const { data: districtAll, refetch: getDistricts } = useDistricts({
    countryId: 1,
    type: 2,
    parent_code: "",
  });
  const { data: wardAll, refetch: getWards } = useWards({
    countryId: 1,
    type: 3,
    parent_code: "",
  });

  const { data: locationsCustomer, refetch: refetchLocationCustomer } =
    useLocationsByCustomer({
      customerId: locationParams?.customerId,
      params: locationParams,
    });

  const handleChangeCustomer = customerId => {
    setLocationParams(prev => ({
      ...prev,
      customerId: customerId,
    }));
  };

  const handleAfterOrderCreated = _data => {
    console.log("🚀 ~ onSuccessCreateOrder ~ _data:", _data);
    if (
      _data.shipping_type === SHIPPING_TYPE.NHAXE ||
      customerCode === "GSNEW"
    ) {
      setOrderCode(_data.order_code);
      const mes = [];
      if (_data.shipping_type === SHIPPING_TYPE.NHAXE) {
        mes.push({
          value: t("message.note", {
            order_code: _data.order_code,
          }),
          class: "",
        });
      }
      if (customerCode === "GSNEW") {
        mes.push({
          value: t("message.note_create_GSNEW"),
          class: "text-danger",
        });
      }
      setMessagePopups(mes);
      setOpenPopup(true);
    } else if (_data.shipping_type === SHIPPING_TYPE.DVVC) {
      const mes = [];
      mes.push({
        value: t("message.note_shipping_type"),
        class: "",
      });
      setMessagePopups(mes);
      setOpenPopup(true);
    } else if (
      _data.shipping_type === SHIPPING_TYPE.RUNNER &&
      !_data?.check_have_runner
    ) {
      const mes = [];
      mes.push({
        value: t("message.running_schedule_empty"),
        class: "text-warning",
      });
      setMessagePopups(mes);
      setOpenWarningRunnerPopup(true);
    } else {
      navigate("/my-orders");
    }
  };

  const onSuccessCreateOrder = _data => {
    handleAfterOrderCreated(_data);
  };

  const onErrorCreateOrder = _data => { };

  const { mutate: createOrder, isLoading: isCreateOrder } = useCreateOrder(
    onSuccessCreateOrder,
    onErrorCreateOrder
  );

  const handleCreateOrder = data => {
    setOrderCode("");
    setCustomerCode(data.customer_code);
    createOrder({
      ...data,
      ticket_id: hasTicketContext ? ticketIdForOrder : undefined,
    });
  };

  useEffect(() => {
    const { customerId } = locationParams;
    if (customerId) {
      refetchLocationCustomer();
    }
  }, [locationParams, refetchLocationCustomer]);

  useEffect(() => {
    refetchProvinces();
    getDistricts();
    getWards();
  }, []);

  useEffect(() => {
    if (provinceParams?.type === 1) refetchProvinces();
  }, [provinceParams, refetchProvinces]);

  useEffect(() => {
    if (districtParams?.type === 2) refetchDistricts();
  }, [districtParams, refetchDistricts]);

  useEffect(() => {
    if (wardParams?.type === 3) refetchWards();
  }, [wardParams, refetchWards]);

  const toggleModalNotify = () => {
    setOpenPopup(!openPopup);
    handleCancel();
  };

  const toggleOpenWarningRunnerPopup = () => {
    setOpenWarningRunnerPopup(!openWarningRunnerPopup);
    handleCancel();
  };

  const handleCancel = () => {
    navigate("/my-orders");
  };
  const handleLocationKeywordsChange = value => {
    setLocationParams(prev => ({ ...prev, keywords: value }));
  };
  const handleCustomerKeywordsChange = value => {
    setCustomerParams(prev => ({ ...prev, keywords: value }));
  };

  const copied_order = "copied_order";
  const copiedOrder = localStorage.getItem(copied_order);
  if (copiedOrder) {
    const copiedOrderData = JSON.parse(copiedOrder);
    const { customer_id: customerId } = copiedOrderData;
    setCoppiedOrder(copiedOrderData);
    if (customerId) {
      setCustomerDetailParams(prev => ({
        ...prev,
        customerId: customerId,
      }));
    }
    localStorage?.removeItem(copied_order);
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <MessageModal
          isOpen={openPopup}
          autoFocus={true}
          centered={true}
          toggle={toggleModalNotify}
          clickOutsideNotClose={true}
          type={"infor"}
          message={t("message.note", {
            order_code: orderCode,
          })}
          messages={messagePopups}
        ></MessageModal>

        <MessageModal
          isOpen={openWarningRunnerPopup}
          autoFocus={true}
          centered={true}
          toggle={toggleOpenWarningRunnerPopup}
          clickOutsideNotClose={true}
          type={"infor"}
          messages={messagePopups}
        ></MessageModal>
        <MetaTags>
          <title>{t("title.create")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.create")}
            breadcrumbItems={[
              { name: t("title.my_order") },
              { name: t("title.list"), link: "/my-orders" },
              { name: t("title.create") },
            ]}
          />
        </div>
        <Container fluid={true}>
          {!isTicketDetailReady ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <OrdersFormV2
              isMyOrder
              isHiddenShippingUnit={isHiddenShippingUnit}
              customers={customers?.items}
              locationsCustomer={locationsCustomer?.items}
              handleChangeCustomer={handleChangeCustomer}
              provinces={provinces}
              districts={districts}
              wards={wards}
              districtAll={districtAll}
              wardAll={wardAll}
              createOrder={handleCreateOrder}
              onChangeProvince={value => {
                setDistrictParams(prev => ({
                  ...prev,
                  type: 2,
                  parent_code: value,
                }));
              }}
              onChangeDistrict={value => {
                setWardParams(prev => ({ ...prev, type: 3, parent_code: value }));
              }}
              onKeywordsChange={handleCustomerKeywordsChange}
              refetchLocationCustomer={refetchLocationCustomer}
              handleCancel={handleCancel}
              onLocationKeywordsChange={handleLocationKeywordsChange}
              isCreateOrder={isCreateOrder}
              copiedOrder={coppiedOrder}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MyOrderPageV2;
